import { useState, type ReactNode } from "react";
import { Button } from "@/components/input/Button";
import ErrorBox from "@/components/input/ErrorBox";
import { useAuthStore } from "@/services/Cognito/authStore";
import { handleSignIn } from "@/services/Cognito/handlers";
import { useForm, type SubmitHandler } from "react-hook-form";
import { SUPPORT_EMAIL } from "./constants";

type FormInputs = {
  email: string;
  password: string;
};

type Props = {
  header?: ReactNode;
  footer?: ReactNode;
};

const DefaultHeader = () => {
  return (
    <>
      <h3 className="text-left text-3xl font-semibold">Sign In</h3>
      <hr className="my-4 border-gray-900"></hr>
    </>
  );
};

const DefaultFooter = () => {
  const setRoute = useAuthStore((ctx) => ctx.setRoute);
  const setEmail = useAuthStore((ctx) => ctx.setSignUpEmail);
  return (
    <>
      <div className="flex justify-center text-sm">
        <span className="pr-2">Dont have an account?</span>
        <a
          className="cursor-pointer font-bold text-[#6852c0] hover:text-[#7862d0]"
          onClick={() => setRoute("signUp")}
        >
          Sign Up
        </a>
      </div>
      <a
        className="flex cursor-pointer justify-center text-sm  font-bold text-[#6852c0] hover:text-[#7862d0]"
        onClick={() => {
          setEmail(undefined);

          setRoute("resetPassword");
        }}
      >
        Forgot your Password?
      </a>
    </>
  );
};
export const SignInForm = (props: Props) => {
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const { register, handleSubmit } = useForm<FormInputs>();
  const setRoute = useAuthStore((ctx) => ctx.setRoute);
  const setSignUpEmail = useAuthStore((ctx) => ctx.setSignUpEmail);
  const hideAuthenticator = useAuthStore((ctx) => ctx.hideAuthenticator);

  const onSubmit: SubmitHandler<FormInputs> = async (form) => {
    try {
      const res = await handleSignIn({
        username: form.email,
        password: form.password,
      });
      if (res.isSignedIn) return hideAuthenticator();
      switch (res.nextStep.signInStep) {
        case "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE":
        case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
          console.error(`${res.nextStep.signInStep} not handled`);
          break;
        case "CONFIRM_SIGN_UP":
          setPendingEmail(form.email);
          setShowVerificationPrompt(true);
          break;
        case "RESET_PASSWORD":
          setRoute("resetPassword");
          break;
        case "DONE":
          hideAuthenticator();
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        // Map Cognito error messages to user-friendly messages
        const friendlyMessages: Record<string, string> = {
          "Incorrect username or password": "Incorrect email or password. Please try again.",
          "User does not exist": "No account found with this email. Please check your email or sign up.",
          "Password attempts exceeded": "Too many failed attempts. Please try again later or reset your password.",
          "User is disabled": `This account has been disabled. Please contact ${SUPPORT_EMAIL} for assistance.`,
        };

        // Find matching error message
        const matchingKey = Object.keys(friendlyMessages).find((key) =>
          error.message.includes(key)
        );

        const friendlyMessage = matchingKey ? friendlyMessages[matchingKey] : error.message;
        setSubmitError(`${friendlyMessage} Need help? Contact ${SUPPORT_EMAIL}`);
      } else {
        console.error(error);
        setSubmitError(`An unexpected error occurred. Please try again or contact ${SUPPORT_EMAIL} for help.`);
      }
    }
  };

  const handleContinueToVerification = () => {
    setSignUpEmail(pendingEmail);
    setRoute("verifyEmail");
  };

  // Show verification prompt if user needs to verify email
  if (showVerificationPrompt) {
    return (
      <div className="flex w-full flex-col rounded-xl border border-gray-300 bg-white px-8 pb-4 pt-8 sm:w-[32rem]">
        <h3 className="mb-4 text-2xl font-semibold">Verify Your Email</h3>
        <div data-testid="verification-prompt" className="mb-4">
          <p className="mb-2">
            Your email address needs to be verified before you can sign in.
          </p>
          <p className="mb-1">
            Click the button below to send a verification code to:
          </p>
          <p className="break-all font-semibold">{pendingEmail}</p>
        </div>
        <button
          onClick={handleContinueToVerification}
          className="mb-3 rounded-lg bg-[#6852c0] px-4 py-2.5 font-semibold text-white hover:bg-[#7862d0]"
        >
          Continue to Verification
        </button>
        <button
          onClick={() => {
            setShowVerificationPrompt(false);
            setPendingEmail("");
          }}
          className="rounded-lg border border-gray-300 bg-transparent px-4 py-2 text-gray-700 hover:bg-gray-100"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col rounded-xl border border-gray-300 bg-white px-8 pb-4 pt-8 sm:w-[32rem]">
      {props.header || <DefaultHeader />}

      <form
        className="flex w-full flex-col space-y-3"
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
      >
        <div className="grid space-y-2">
          <label>Email</label>
          <input
            type="email"
            className="rounded border border-gray-900 p-2 text-center"
            placeholder="Enter your Email"
            {...register("email", { required: true })}
          />
        </div>
        <div className="grid space-y-2">
          <label>Password</label>
          <div className="flex w-full">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full rounded border border-gray-900 p-2 text-center"
              placeholder="Enter your Password"
              {...register("password", { required: true })}
            />
          </div>
        </div>
        <div
          className="flex select-none space-x-2"
          onClick={() => {
            setShowPassword(!showPassword);
          }}
        >
          <input
            type="checkbox"
            className="scale-125"
            checked={showPassword}
            readOnly
          />
          <label>Show Password</label>
        </div>
        <Button type="submit">Sign in</Button>
        {submitError && (
          <ErrorBox
            message={submitError}
            hide={() => setSubmitError(undefined)}
          />
        )}
        <div>{props.footer || <DefaultFooter />}</div>
      </form>
    </div>
  );
};

export default SignInForm;
